import { useDisclosure } from '@mantine/hooks';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { openCodeDialogOpIdAtom } from '~/atoms/no-code-atoms';
import { EditCodeModal } from '~/components/codeEditor/CodeEditor';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { api } from '~/trpc-extra/api';
import CodeBlockInner from '../CodeBlockInner';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export function CodeBlockOp({
  step,
  operation,
  previewCursorAllowed,
  order,
  code,
  description,
  readonly,
  disabled,
  selected,
  setEditing,
  disableMenu,
  testContentId,
  stepIdx,
  parentContexts,
}: {
  step: SelectedStep;
  operation: SelectedOperation;
  previewCursorAllowed: boolean;
  order?: number;
  code: string;
  description?: string;
  readonly?: boolean;
  disabled?: boolean;
  selected?: boolean;
  setEditing: (isEditing: boolean) => void;
  disableMenu?: boolean;
  testContentId: string;
  stepIdx: number;
  parentContexts?: OperationParentContext[];
}) {
  const trpcUtils = api.useUtils();
  const testVariables = useTestVariables({
    testContentId,
    limit: { type: 'operationId', id: operation.id },
    parentContexts,
  });

  const parentContext = parentContexts?.at(-1);

  const [openCodeDialogOpId, setOpenCodeDialogOpId] = useAtom(openCodeDialogOpIdAtom);
  const [codeEditorOpened, codeEditorOps] = useDisclosure(false);
  const upsertOperation = api.operation.upsertOperation.useMutation();

  useEffect(() => {
    if (openCodeDialogOpId === operation?.id) {
      codeEditorOps.open();
      setOpenCodeDialogOpId(undefined);
    }
  }, [openCodeDialogOpId, operation?.id, codeEditorOps, setOpenCodeDialogOpId]);

  const handleOpenCodeEditor = () => {
    setEditing(true);
    codeEditorOps.open();
  };

  return (
    <div className="w-full text-sm">
      <EditCodeModal
        testContentId={testContentId}
        readOnly={readonly || !!disabled}
        initialCode={code || ''}
        initialDescription={description}
        showDescription={false}
        opened={codeEditorOpened}
        onClose={() => {
          setEditing(false);
          codeEditorOps.close();
        }}
        onSave={async (code: string, description?: string) => {
          await upsertOperation.mutateAsync({
            operation: {
              pageAlias: operation.pageAlias,
              stepId: step.id,
              order,
              operationId: operation?.id,
              parentContext,
              operation: {
                operationType: 'CODE_BLOCK',
                code,
                description,
              },
            },
          });
          await Promise.allSettled([
            trpcUtils.testContent.get.invalidate({ id: testContentId }),
            parentContext?.conditional &&
              trpcUtils.operation.getFullConditionalBlock.invalidate({
                conditionalBlockId: parentContext.conditional.conditionalBlockId,
              }),
            parentContext?.componentId && trpcUtils.component.get.invalidate({ id: parentContext.componentId }),
            trpcUtils.testContent.getCode.invalidate({ testContentId }),
          ]);
        }}
        height="75vh"
        variables={testVariables}
        stepIdx={stepIdx}
        step={step}
      />
      <CodeBlockInner
        readonly={readonly}
        previewCursorAllowed={previewCursorAllowed}
        onClick={handleOpenCodeEditor}
        code={operation.codeBlockOp?.code || '//Custom Code'}
        selected={selected}
        disableMenu={disableMenu}
        testContentId={testContentId}
        stepOrder={step.stepOrder}
        editing={false}
        operation={operation}
      />
    </div>
  );
}
